import { useEffect, useRef } from 'react';

type CloseOnBackNavigationOptions = {
    /**
     * Return `false` when the overlay closes because the page is navigating
     * away: a `history.back()` issued right after `location.assign()` cancels
     * that pending navigation.
     */
    shouldPopHistoryOnClose?: () => boolean;
};

/**
 * Dismisses an open overlay/modal when the user triggers a back navigation
 * (browser back button or Android hardware back button) instead of letting the
 * back press fall through to the underlying page while the overlay stays on top.
 *
 * While the overlay is open we push a dedicated history entry, so the first
 * back press pops that entry and fires `popstate`, which closes the overlay.
 * If the overlay is instead closed by an in-app action, we pop the entry we
 * added so no dead history entry is left behind.
 */
export function useCloseOnBackNavigation(
    isOpen: boolean,
    onClose: () => void,
    { shouldPopHistoryOnClose }: CloseOnBackNavigationOptions = {},
) {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const shouldPopHistoryOnCloseRef = useRef(shouldPopHistoryOnClose);
    shouldPopHistoryOnCloseRef.current = shouldPopHistoryOnClose;

    useEffect(() => {
        if (!isOpen || typeof window === 'undefined') return;

        window.history.pushState({ acOverlayOpen: true }, '');

        const handlePopState = () => {
            onCloseRef.current();
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            const shouldPopHistory = shouldPopHistoryOnCloseRef.current?.() ?? true;
            if (shouldPopHistory && window.history.state?.acOverlayOpen) {
                window.history.back();
            }
        };
    }, [isOpen]);
}
